/* eslint-disable max-lines-per-function, max-lines */
import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { TableUtility } from '@utility/dynamic-table';
import { Navigate } from 'react-router-dom';
import lottie from 'lottie-web';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import Loader from '../../../Components/Loader';
import EmployeeProfileHeader from
  '../../Resource/ResourceManagement/components/EmployeeProfileHeader/employeeProfileHeader';
import {
  projectRightsBaseURL,
  getProjectRightsFilters,
  updateProjectAccessRequest,
  exportProjectRightsRequest,
} from '../../../Api/request/projectRights';
import { MODULE_NAMES } from '../../../Constants/modules';
import exportToExcel from '../../../Utils/exportToExcel';
import './ProjectRights.scss';


const TABLE_DATA_COLS = [
  { field: 'projectId', header: 'Project Id', width: 100, order: 1 },
  { field: 'projectName', header: 'Project Name', width: 220, order: 2 },
  { field: 'projectRegion', header: 'Project Region', width: 140, order: 3 },
  { field: 'projectCountry', header: 'Project Country', width: 150, order: 4 },
  { field: 'projectVertical', header: 'Project Vertical', width: 150, order: 5 },
  { field: 'projectType', header: 'Project Type', width: 130, order: 6 },
  { field: 'projectStatus', header: 'Project Status', width: 140, order: 7 },
];
const PAGE_SIZE = Number.MAX_SAFE_INTEGER;
const STATUS_CLASS_MAP = {
  'in-progress': 'in-progress',
  completed: 'completed',
  'on-hold': 'on-hold',
  active: 'active',
  inactive: 'inactive',
  closed: 'closed',
  dropped: 'dropped',
};


const getStatusClass = (status = '') => (
  STATUS_CLASS_MAP[status.toLowerCase().replace(/[\s_]+/g, '-')] || 'default'
);
const formatStatusText = (status = '') => {
  const clean = status.trim();
  if (clean.toLowerCase() === 'in progress') return 'In-Progress';
  if (clean.toLowerCase() === 'on hold') return 'On-Hold';
  return clean;
};
const hasAccessFlag = (proj) => {
  const val = proj.hasAccess ?? proj.has_access ?? proj.hasaccess;
  return val === true || val === 1 || val === '1' || val === 'true';
};
const toOpt = (item) => ({
  label: item.name ?? item.employee_name ?? item.employeeName ?? item.fullName ?? item.label ?? String(item.id ?? ''),
  value: String(item.id ?? item.emp_id ?? item.empId ?? item.verticalId ?? item.typeId ?? item.statusId ?? ''),
});
const arraysEqual = (first, second) => {
  if (first === second) return true;
  const arrA = Array.isArray(first) ? first : (first ? [first] : []);
  const arrB = Array.isArray(second) ? second : (second ? [second] : []);
  if (arrA.length !== arrB.length) return false;
  return arrA.every((val, index) => val === arrB[index]);
};
const filterValuesEqual = (first, second) => Object.keys(first).every((key) => (
  arraysEqual(first[key], second[key])
));

const mkFilterCol = (field, header, order, placeholder, options, filterUrl, isMulti = false, isRequired = false) => {
  return {
    field,
    header,
    width: 180,
    filter: true,
    visible: true,
    sortable: false,
    pinnable: false,
    order,
    defaultPinned: false,
    isGlobalFilter: true,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    isRequired,
    isMandatory: isRequired,
    menuItems: [{
      label: header,
      items: [{
        type: 'filter',
        field,
        label: header,
        placeholder,
        template: isMulti ? 'multiSelect' : 'singleSelect',
        selectedValue: null,
        options,
        values: options,
        url: filterUrl || undefined,
        isRequired,
        isMandatory: isRequired,
      }],
    }],
  };
};
const mkTableCol = (field, header, width, order) => ({
  field,
  header,
  width,
  filter: false,
  visible: true,
  sortable: false,
  pinnable: false,
  order,
  defaultPinned: false,
  showFilterButtonInHeader: false,
  showPinButtonInHeader: false,
});
const uniqOpts = (opts) => {
  const seen = new Set();
  return opts.filter((opt) => {
    const cleanLabel = String(opt.label || '').trim()
      .toLowerCase();
    if (!cleanLabel || seen.has(cleanLabel)) return false;
    seen.add(cleanLabel);
    return true;
  });
};
const buildToolbarCols = ({ employees = [], verticals = [], types = [], statuses = [], projects = [] } = {}) => {
  const projNames = projects.map(toOpt);
  const projVerticals = verticals.map(toOpt);
  const projTypes = types.map(toOpt);
  const projStatuses = statuses.map(toOpt);
  const accessOptions = [
    { label: 'With Access', value: '1' },
    { label: 'Without Access', value: '0' },
  ];

  return [
    mkFilterCol(
      'empId',
      'Employee Name',
      1,
      'Select Employee',
      uniqOpts(employees.map(toOpt)),
      `${projectRightsBaseURL}/filters/employees`,
      false,
      true,
    ),
    mkFilterCol('projectName', 'Project Name', 2, 'All Projects', uniqOpts(projNames), null, false),
    mkFilterCol('projectVertical', 'Project Vertical', 3, 'All Verticals', uniqOpts(projVerticals), null, true),
    mkFilterCol('projectType', 'Project Type', 4, 'All Types', uniqOpts(projTypes), null, true),
    mkFilterCol('projectStatus', 'Project Status', 5, 'All Statuses', uniqOpts(projStatuses), null, true),
    mkFilterCol('verticalId', 'Employee Vertical', 6, 'All Verticals', uniqOpts(verticals.map(toOpt)), null, true),
    mkFilterCol('typeId', 'Employee Type', 7, 'All Types', uniqOpts(types.map(toOpt)), null, true),
    mkFilterCol('statusId', 'Employee Status', 8, 'All Statuses', uniqOpts(statuses.map(toOpt)), null, true),
    mkFilterCol('accessType', 'Access Type', 9, 'All', accessOptions, null, true),
  ];
};

const isResetClick = (target) => {
  if (!target || !target.closest) return false;
  const btn = target.closest('button');
  if (!btn) return false;

  // Enforce that the reset button belongs to the main toolbar's .button-group
  if (!btn.closest('.button-group')) return false;

  const hasRefreshIcon = Boolean(btn.querySelector('.pi-refresh') || btn.querySelector('.pi-replay'));
  const hasResetAttr = (btn.getAttribute('aria-label') || '').toLowerCase().includes('reset')
    || (btn.getAttribute('title') || '').toLowerCase().includes('reset');

  return hasRefreshIcon || hasResetAttr;
};
const resolveEmpId = (emp) => emp?.employeeId || emp?.empId || emp?.id || '';

const loadFilterOptionsSafe = async (onSuccess) => {
  try {
    const resp = await UseAxios(getProjectRightsFilters(), () => {});
    const payload = resp?.data?.data || resp?.data || {};
    if (payload) {
      onSuccess({
        employees: payload.employees || [],
        verticals: payload.verticals || [],
        types: payload.types || [],
        statuses: payload.statuses || [],
        projects: payload.projects || [],
      });
    }
  } catch { /* silently ignore */ }
};


const buildToolbarConfiguration = (
  userProfile,
  tokenVal,
  handleFilteredApiResponse,
  toolbarCols,
  handleTableState,
) => ({
  generalSettings: {
    enableApiFiltering: true,
    baseURL: projectRightsBaseURL,
    showToolBar: true,
    showPagination: false,
    showTieredMenu: true,
    userInfo: {
      emailId: userProfile?.email || '',
      permissions: userProfile?.permissions || [],
      roleDetails: userProfile?.roleDetails || {},
      employeeId: userProfile?.employeeId || '',
    },
    headers: { token: tokenVal },
    additionalPayloadData: {},
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: true },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
      download: {
        endpoint: 'export',
        enabled: true,
        transformResponse: (res) => {
          const body = res?.data || res || {};
          const payload = body.data || body || {};
          const rawRows = payload.projects || payload.tableData || [];
          const exportRows = rawRows.map((row) => {
            const hasAccessVal = row.hasAccess ?? row.has_access ?? row.hasaccess;
            const isAccessible = hasAccessVal === true
              || hasAccessVal === 1
              || hasAccessVal === '1'
              || hasAccessVal === 'true';
            return {
              projectId: row.projectId || row.project_id || row.id || '',
              projectName: row.projectName || row.project_name || '',
              projectRegion: row.projectRegion || row.region_name || row.project_region || '',
              projectCountry: row.projectCountry || row.country_name || row.project_country || '',
              projectVertical: row.projectVertical || row.vertical || row.project_vertical || '',
              projectType: row.projectType || row.project_type_name || row.project_type || '',
              projectStatus: row.projectStatus || row.project_status_name || row.project_status || '',
              hasAccess: isAccessible ? 'Yes' : 'No',
            };
          });

          const exportColumnsConfig = [
            { header: 'Project ID', field: 'projectId', width: 15 },
            { header: 'Project Name', field: 'projectName', width: 30 },
            { header: 'Project Region', field: 'projectRegion', width: 20 },
            { header: 'Project Country', field: 'projectCountry', width: 20 },
            { header: 'Project Vertical', field: 'projectVertical', width: 20 },
            { header: 'Project Type', field: 'projectType', width: 20 },
            { header: 'Project Status', field: 'projectStatus', width: 15 },
            { header: 'Has Access', field: 'hasAccess', width: 15 },
          ];

          return {
            tableConfiguration: exportColumnsConfig,
            tableData: exportRows,
          };
        },
        filename: () => {
          const now = new Date();
          const suffix = `${now.getDate()}-${now.getMonth() + 1}-${now.getFullYear()}`;
          return `Project_Rights-${suffix}`;
        },
        sheetName: () => 'Project Rights',
        reportTitle: () => 'Project Rights Report',
      },
    },
    toolBarSettings: {
      showAddNew: false,
      showDownloadExcel: true,
      showColumnCustomize: false,
      showColumnFilterMenu: true,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showReset: false,
      showExpandCollapse: false,
      showFilterOption: true,
      showToolBarHeading: true,
      toolBarHeading: 'Project Rights',
    },
    handler: { handleFilteredApiResponse, setTableStateHandler: handleTableState },
  },
  tableSettings: {
    size: 'small',
    pageSize: PAGE_SIZE,
    tableConfig: toolbarCols,
    tableScrollHeight: '0px',
    loaderSkeleton: { rows: 0, bodyCells: 1, columns: 1 },
    paginationConfig: undefined,
    showExpandCollapse: false,
    maxFrozenColumns: { desktop: 0, tablet: 0, mobile: 0 },
  },
});

const buildTableConfiguration = (userProfile, tokenVal, tableColsConfig, onPageChange) => ({
  generalSettings: {
    enableApiFiltering: false,
    baseURL: '',
    showToolBar: false,
    showPagination: false,
    userInfo: {
      emailId: userProfile?.email || '',
      permissions: userProfile?.permissions || [],
      roleDetails: userProfile?.roleDetails || {},
      employeeId: userProfile?.employeeId || '',
    },
    headers: { token: tokenVal },
    additionalPayloadData: {},
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: false },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
      download: { endpoint: '', enabled: false },
    },
    toolBarSettings: {
      showAddNew: false,
      showDownloadExcel: false,
      showColumnCustomize: false,
      showColumnFilterMenu: false,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showReset: false,
      showExpandCollapse: false,
      showFilterOption: false,
      showToolBarHeading: false,
      toolBarHeading: '',
    },
    handler: {
      handleFilteredApiResponse: () => {},
      setTableStateHandler: (stateValue) => {
        const resolvedState = typeof stateValue === 'function' ? stateValue() : stateValue;
        if (resolvedState?.pagination && typeof onPageChange === 'function') {
          onPageChange(resolvedState.pagination.page || 1);
        }
      },
    },
  },
  tableSettings: {
    size: 'small',
    pageSize: PAGE_SIZE,
    tableConfig: tableColsConfig,
    tableScrollHeight: 'auto',
    loaderSkeleton: { rows: 5, bodyCells: 8, columns: 8 },
    paginationConfig: { page: 1, limit: PAGE_SIZE },
    showExpandCollapse: false,
    maxFrozenColumns: { desktop: 0, tablet: 0, mobile: 0 },
  },
});

const getSelectedFilterValues = (tableState) => {
  const values = {
    empId: null,
    projectName: null,
    projectVertical: null,
    projectType: null,
    projectStatus: null,
    verticalId: null,
    typeId: null,
    statusId: null,
    accessType: null,
  };
  const labels = {
    empId: null,
    projectName: null,
    projectVertical: null,
    projectType: null,
    projectStatus: null,
    verticalId: null,
    typeId: null,
    statusId: null,
    accessType: null,
  };
  const config = tableState?.tableConfiguration || [];
  config.forEach((col) => {
    const filterItem = col?.menuItems?.[0]?.items?.[0];
    if (filterItem && filterItem.field) {
      const selected = filterItem.selectedValue ?? filterItem.selectedValues ?? null;
      let val = null;
      let lbl = null;
      if (selected !== null && selected !== undefined) {
        if (Array.isArray(selected)) {
          val = selected.map(item => {
            if (typeof item === 'object' && item !== null) {
              return String(item.value ?? item.id ?? item.label ?? '');
            }
            return String(item);
          }).filter(Boolean);
          lbl = selected.map(item => {
            if (typeof item === 'object' && item !== null) {
              return String(item.label ?? item.name ?? item.value ?? '');
            }
            return String(item);
          }).filter(Boolean);
        } else if (typeof selected === 'object') {
          val = selected.value ?? selected.id ?? selected.label ?? selected.name ?? null;
          val = val !== null ? String(val) : null;
          lbl = selected.label ?? selected.name ?? selected.value ?? null;
          lbl = lbl !== null ? String(lbl) : null;
        } else {
          val = String(selected);
          lbl = String(selected);
        }
      }
      values[filterItem.field] = val;
      labels[filterItem.field] = lbl;
    }
  });
  return { values, labels };
};

const ProjectRights = () => {
  const { userProfile, authToken } = useAuth();
  const { snackbarOpen } = useSnackbar();
  const toolbarContainerRef = useRef(null);
  const headerCheckboxRef = useRef(null);
  const rowCheckboxRefs = useRef({});
  const projectsRef = useRef([]);
  const employeeDataRef = useRef(null);

  const lottieContainerRef = useRef(null);
  const lottieAnimRef = useRef(null);
  const activeFiltersRef = useRef({});
  const allFilterOptionsRef = useRef({ employees: [], verticals: [], types: [], statuses: [] });
  const prevFiltersRef = useRef({
    empId: null,
    projectName: null,
    projectVertical: null,
    projectType: null,
    projectStatus: null,
    verticalId: null,
    typeId: null,
    statusId: null,
    accessType: null,
  });

  const [toolbarResetNonce, setToolbarResetNonce] = useState(0);
  const [tableNonce, setTableNonce] = useState(0);
  const [toolbarCols, setToolbarCols] = useState(() => buildToolbarCols());

  const [employeeData, setEmployeeData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const dropdownAnchorRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});

  // Sync projectsRef with projects
  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);


  const filteredProjects = useMemo(() => {
    return projects.filter((proj) => {
      const pId = proj.projectId || proj.project_id || proj.id;
      const pName = proj.projectName || proj.project_name || '';
      const pVerticalName = proj.projectVertical || proj.vertical || proj.project_vertical || '';
      const pVerticalId = proj.vertical_id || proj.verticalId || '';
      const pTypeName = proj.projectType || proj.project_type_name || proj.project_type || '';
      const pTypeId = proj.project_type_id || proj.projectTypeId || proj.typeId || '';
      const pStatusName = proj.projectStatus || proj.project_status_name || proj.project_status || '';
      const pStatusId = proj.project_status_id || proj.projectStatusId || proj.statusId || '';
      const hasAccess = hasAccessFlag(proj);

      if (activeFilters.projectName) {
        const val = String(activeFilters.projectName);
        if (String(pId) !== val && pName !== val) return false;
      }

      if (activeFilters.projectVertical && activeFilters.projectVertical.length > 0) {
        const matched = activeFilters.projectVertical.some(val =>
          String(pVerticalId) === String(val) || pVerticalName === String(val),
        );
        if (!matched) return false;
      }

      if (activeFilters.projectType && activeFilters.projectType.length > 0) {
        const matched = activeFilters.projectType.some(val =>
          String(pTypeId) === String(val) || pTypeName === String(val),
        );
        if (!matched) return false;
      }

      if (activeFilters.projectStatus && activeFilters.projectStatus.length > 0) {
        const matched = activeFilters.projectStatus.some(val =>
          String(pStatusId) === String(val) || pStatusName === String(val),
        );
        if (!matched) return false;
      }

      if (activeFilters.accessType && activeFilters.accessType.length > 0) {
        const hasAccessStr = hasAccess ? '1' : '0';
        if (!activeFilters.accessType.includes(hasAccessStr)) return false;
      }
      return true;
    });
  }, [projects, activeFilters]);

  const visibleProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredProjects.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredProjects, currentPage]);

  // Intercept native TableUtility download button clicks
  useEffect(() => {
    const container = toolbarContainerRef.current;
    if (!container) return;
    const handler = (event) => {
      // The native download button wraps an SVG with a specific path (download arrow icon)
      const btn = event.target.closest('button');
      if (!btn) return;
      const svg = btn.querySelector('svg');
      if (!svg) return;
      // Check it's the download icon by looking for its known path data fragment
      const path = svg.querySelector('path');
      if (!path) return;
      const pathData = path.getAttribute('d') || '';
      if (!pathData.includes('16.5672')) return; // unique fragment in the download arrow SVG
      event.stopPropagation();
      event.preventDefault();
      if (!isFilterApplied) return;
      dropdownAnchorRef.current = btn;
      const rect = btn.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
      setShowDropdown((prev) => !prev);
    };
    container.addEventListener('click', handler, true);
    return () => container.removeEventListener('click', handler, true);
  }, [isFilterApplied]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.pr-download-popup')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  const triggerExport = async (verticalId, typeId, statusId) => {
    try {
      const empId = resolveEmpId(employeeDataRef.current);
      if (!empId) return;

      const req = exportProjectRightsRequest({
        empId,
        verticalId,
        typeId,
        statusId,
      });

      const res = await UseAxios(req);
      const body = res?.data || res || {};
      const payload = body.data || body || {};
      const rawRows = payload.projects || payload.tableData || [];

      if (!rawRows.length) {
        snackbarOpen('No data to export.', 'info');
        return;
      }

      const exportRows = rawRows.map((row) => {
        const hasAccessVal = row.hasAccess ?? row.has_access ?? row.hasaccess;
        const isAccessible = hasAccessVal === true
          || hasAccessVal === 1
          || hasAccessVal === '1'
          || hasAccessVal === 'true';
        return {
          projectId: row.projectId || row.project_id || row.id || '',
          projectName: row.projectName || row.project_name || '',
          projectRegion: row.projectRegion || row.region_name || row.project_region || '',
          projectCountry: row.projectCountry || row.country_name || row.project_country || '',
          projectVertical: row.projectVertical || row.vertical || row.project_vertical || '',
          projectType: row.projectType || row.project_type_name || row.project_type || '',
          projectStatus: row.projectStatus || row.project_status_name || row.project_status || '',
          hasAccess: isAccessible ? 'Yes' : 'No',
        };
      });

      const exportColumnsConfig = [
        { header: 'Project ID', key: 'projectId', width: 15 },
        { header: 'Project Name', key: 'projectName', width: 30 },
        { header: 'Project Region', key: 'projectRegion', width: 20 },
        { header: 'Project Country', key: 'projectCountry', width: 20 },
        { header: 'Project Vertical', key: 'projectVertical', width: 20 },
        { header: 'Project Type', key: 'projectType', width: 20 },
        { header: 'Project Status', key: 'projectStatus', width: 15 },
        { header: 'Has Access', key: 'hasAccess', width: 15 },
      ];

      const now = new Date();
      const suffix = `${now.getDate()}-${now.getMonth() + 1}-${now.getFullYear()}`;

      await exportToExcel({
        data: exportRows,
        workBookTitle: `Project_Rights-${suffix}`,
        workSheetTitle: 'Project Rights',
        columnsConfig: exportColumnsConfig,
        borderAllCells: true,
      });

    } catch (err) {
      console.error('Export error:', err);
      snackbarOpen('Failed to export data.', 'warning');
    }
  };

  const handleDownloadSearchedData = () => {
    setShowDropdown(false);
    triggerExport(
      prevFiltersRef.current.verticalId,
      prevFiltersRef.current.typeId,
      prevFiltersRef.current.statusId,
    );
  };

  const handleDownloadAllData = () => {
    setShowDropdown(false);
    triggerExport(null, null, null);
  };

  projectsRef.current = projects;

  const tokenVal = localStorage.getItem('authToken')
    || userProfile?.access_token
    || authToken
    || '';
  const isLoaded = Boolean(tokenVal || userProfile?.email);

  const canUpdate = useMemo(() => {
    const isSuperAdmin = userProfile?.roleDetails?.some((role) => role.roleName === 'Super Admin');
    if (isSuperAdmin) return true;
    const userPermissions = userProfile?.permissions?.[MODULE_NAMES.RESOURCE_PROJECT_RIGHTS] || [];
    return userPermissions.includes('update')
      || userPermissions.includes('create')
      || userPermissions.includes('write');
  }, [userProfile]);

  const canView = useMemo(() => {
    const isSuperAdmin = userProfile?.roleDetails?.some((role) => role.roleName === 'Super Admin');
    if (isSuperAdmin) return true;
    const userPermissions = userProfile?.permissions?.[MODULE_NAMES.RESOURCE_PROJECT_RIGHTS] || [];
    return userPermissions.length > 0;
  }, [userProfile]);

  const handleSelectAll = useCallback((checked) => {
    const visibleIds = visibleProjects.map((proj) => String(proj.projectId || proj.project_id || proj.id));
    setCheckedIds((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => {
        if (rowCheckboxRefs.current[id]) rowCheckboxRefs.current[id].checked = checked;
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, [visibleProjects]);

  const handleRowCheckbox = useCallback((projectId, isChecked) => {
    const idStr = String(projectId);
    if (rowCheckboxRefs.current[idStr]) rowCheckboxRefs.current[idStr].checked = isChecked;
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (isChecked) next.add(idStr);
      else next.delete(idStr);
      return next;
    });
  }, []);

  const allChecked = visibleProjects.length > 0 && visibleProjects.every(
    (proj) => checkedIds.has(String(proj.projectId || proj.project_id || proj.id)),
  );
  const someChecked = !allChecked && visibleProjects.some(
    (proj) => checkedIds.has(String(proj.projectId || proj.project_id || proj.id)),
  );

  useEffect(() => {
    let active = true;

    const mountCheckbox = () => {
      if (!active) return;
      const headerCell = document.querySelector('.project-rights-table-utility.table-only thead th:first-child');
      if (!headerCell) return;

      let checkbox = headerCell.querySelector('input[type="checkbox"]');
      if (!checkbox) {
        headerCell.innerHTML = '';
        checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.setAttribute('aria-label', 'Select all');
        headerCell.appendChild(checkbox);
      }

      checkbox.onchange = (evt) => {
        handleSelectAll(evt.target.checked);
      };

      checkbox.disabled = !canUpdate;
      checkbox.checked = allChecked;
      checkbox.indeterminate = someChecked;
      headerCheckboxRef.current = checkbox;
    };

    mountCheckbox();

    // Remount whenever the table DOM changes (e.g. pagination, sorting)
    const observer = new MutationObserver(mountCheckbox);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [allChecked, someChecked, canUpdate, handleSelectAll, isFilterApplied, tableNonce]);

  useEffect(() => {
    loadFilterOptionsSafe((opts) => {
      allFilterOptionsRef.current = opts;
      setToolbarCols(buildToolbarCols(opts, projectsRef.current));
      setToolbarResetNonce((nonce) => nonce + 1);
    });
  }, []);

  useEffect(() => {
    if (!lottieContainerRef.current || isFilterApplied) return;
    import('../../../Assets/images/json/dar_milestone_details_load.json').then(
      ({ default: animationData }) => {
        if (lottieAnimRef.current) {
          lottieAnimRef.current.destroy();
        }
        lottieAnimRef.current = lottie.loadAnimation({
          container: lottieContainerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData,
        });
      },
    );
    return () => {
      if (lottieAnimRef.current) {
        lottieAnimRef.current.destroy();
        lottieAnimRef.current = null;
      }
    };
  }, [isFilterApplied]);

  const clearData = useCallback(() => {
    employeeDataRef.current = null;
    activeFiltersRef.current = {};
    prevFiltersRef.current = {
      empId: null,
      projectName: null,
      projectVertical: null,
      projectType: null,
      projectStatus: null,
      verticalId: null,
      typeId: null,
      statusId: null,
      accessType: null,
    };
    setActiveFilters({});
    setEmployeeData(null);
    setProjects([]);
    setCheckedIds(new Set());
    setLoadingProjects(false);
    setIsFilterApplied(false);
    setCurrentPage(1);
  }, []);

  const handleTableState = useCallback((getTableState) => {
    if (!getTableState) return;
    const tableState = getTableState();
    const { values: currentFilters } = getSelectedFilterValues(tableState);
    const prevFilters = prevFiltersRef.current;
    const filtersChanged = !filterValuesEqual(prevFilters, currentFilters);
    if (filtersChanged && currentFilters.empId) setLoadingProjects(true);

    // Detect if parent api-level filters changed
    const parentChanged = !arraysEqual(currentFilters.verticalId, prevFilters.verticalId)
      || !arraysEqual(currentFilters.typeId, prevFilters.typeId)
      || !arraysEqual(currentFilters.statusId, prevFilters.statusId);

    if (parentChanged) {
      const shouldResetEmp = prevFilters.empId !== null;

      prevFiltersRef.current = {
        ...currentFilters,
        empId: null,
      };

      if (shouldResetEmp) {
        clearData();
      }
    } else {
      prevFiltersRef.current = { ...currentFilters };
    }

    setActiveFilters((previousFilters) => (
      filterValuesEqual(previousFilters, currentFilters) ? previousFilters : currentFilters
    ));
  }, [clearData]);

  useEffect(() => {
    const listener = (evt) => {
      if (isResetClick(evt.target)) clearData();
    };
    document.addEventListener('click', listener, true);
    return () => document.removeEventListener('click', listener, true);
  }, [clearData]);

  const handleFilteredApiResponse = useCallback((response) => {
    setLoadingProjects(false);
    const body = response?.data || response || {};
    const dataPayload = body.data || body || {};

    const applied = dataPayload.appliedFilters || {};
    activeFiltersRef.current = applied;
    const hasActiveFilter = Boolean(applied.empId);
    if (!hasActiveFilter) {
      clearData();
      return;
    }

    const rawProjectList = dataPayload.projects || dataPayload.tableData || [];
    const empData = dataPayload.employee || dataPayload.employeeDetails || null;
    employeeDataRef.current = empData;
    // Deduplicate project records by unique projectId
    const seenIds = new Set();
    const projectList = rawProjectList.filter((proj) => {
      const pId = String(proj.projectId || proj.project_id || proj.id || '');
      if (!pId || seenIds.has(pId)) return false;
      seenIds.add(pId);
      return true;
    });

    // Reset stale DOM refs — table will remount with new key
    rowCheckboxRefs.current = {};
    setEmployeeData(empData);
    setProjects(projectList);
    setTableNonce((nonce) => nonce + 1);
    setCurrentPage(1);
    setCheckedIds(new Set(
      projectList
        .filter(hasAccessFlag)
        .map((proj) => String(proj.projectId || proj.project_id || proj.id)),
    ));
    setIsFilterApplied(true);
  }, []);


  const checkboxColConfig = useMemo(() => ({
    field: 'checkbox',
    header: '',
    width: 46,
    filter: false,
    visible: true,
    sortable: false,
    pinnable: false,
    order: 0,
    defaultPinned: false,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
  }), []);

  const tableColsConfig = useMemo(() => [
    checkboxColConfig,
    ...TABLE_DATA_COLS.map((col) => mkTableCol(col.field, col.header, col.width, col.order)),
  ], [checkboxColConfig]);

  // tableData contains plain values + JSX cells that are stable across checkbox clicks.
  // IMPORTANT: checkedIds is intentionally NOT in the deps array.
  // Adding checkedIds would create new JSX objects on every click, causing
  // TableUtility to deep-traverse them and hit a Maximum call stack exceeded.
  // Checkboxes are uncontrolled (defaultChecked) so DOM state is managed via rowCheckboxRefs.
  const tableData = useMemo(() => filteredProjects.map((proj) => {
    const pId = proj.projectId || proj.project_id || proj.id;
    const pName = proj.projectName || proj.project_name || '';
    const pStatus = proj.projectStatus || proj.project_status_name || proj.project_status || '';
    const pIdStr = String(pId);
    return {
      checkbox: (
        <input
          ref={(el) => {
            if (el) rowCheckboxRefs.current[pIdStr] = el;
          }}
          type="checkbox"
          aria-label={`Select ${pName || pId}`}
          defaultChecked={checkedIds.has(pIdStr)}
          onChange={(evt) => handleRowCheckbox(pId, evt.target.checked)}
          disabled={!canUpdate}
        />
      ),
      projectId: pId,
      projectName: pName,
      projectRegion: proj.projectRegion || proj.region_name || proj.project_region || '',
      projectCountry: proj.projectCountry || proj.country_name || proj.project_country || '',
      projectVertical: proj.projectVertical || proj.vertical || proj.project_vertical || '',
      projectType: proj.projectType || proj.project_type_name || proj.project_type || '',
      projectStatus: (
        <span className={`pr-status pr-status--${getStatusClass(pStatus)}`}>
          {formatStatusText(pStatus)}
        </span>
      ),
    };
  }), [filteredProjects, handleRowCheckbox, canUpdate]);

  const handleSave = useCallback(async () => {
    const empId = resolveEmpId(employeeDataRef.current);
    if (!empId) return;
    setSaving(true);
    try {
      const visibleProjectIds = projectsRef.current.map((proj) => proj.projectId || proj.project_id || proj.id);
      const req = updateProjectAccessRequest({
        empId,
        selectedProjectIds: Array.from(checkedIds).map(Number),
        visibleProjectIds,
      });
      const res = await UseAxios(req);
      const ok = res?.status >= 200 && res?.status < 300;
      if (ok) {
        snackbarOpen('Project access updated successfully.', 'success');
      } else {
        snackbarOpen('Failed to update. Please try again.', 'warning');
      }
    } catch {
      snackbarOpen('Failed to update. Please try again.', 'warning');
    } finally {
      setSaving(false);
    }
  }, [checkedIds, snackbarOpen]);

  const toolbarConfiguration = useMemo(
    () => buildToolbarConfiguration(
      userProfile,
      tokenVal,
      handleFilteredApiResponse,
      toolbarCols,
      handleTableState,
    ),
    [
      userProfile,
      tokenVal,
      handleFilteredApiResponse,
      toolbarCols,
      handleTableState,
    ],
  );

  const tableConfiguration = useMemo(
    () => buildTableConfiguration(userProfile, tokenVal, tableColsConfig, setCurrentPage),
    [userProfile, tokenVal, tableColsConfig],
  );

  const showResultsView = isFilterApplied || loadingProjects;

  if (!isLoaded) return null;
  if (!canView) return <Navigate to="/home" replace />;

  return (
    <div className={`project-rights-wrapper ${isFilterApplied ? 'pr-filter-applied' : ''}`}>

      {/* Toolbar row with filters and Update button */}
      <div className="project-rights-toolbar-row" ref={toolbarContainerRef} style={{ position: 'relative' }}>
        <div className="project-rights-table-utility toolbar-only">
          <TableUtility key={toolbarResetNonce} configuration={toolbarConfiguration} showFilterOption />
        </div>
        {/* Download popup — fixed to viewport so it escapes overflow containers */}
        {showDropdown && (
          <div
            className="pr-download-popup"
            style={{ top: dropdownPos.top, right: dropdownPos.right }}
          >
            <div
              className="pr-download-item pr-download-item--active"
              onClick={handleDownloadSearchedData}
            >
              Searched Data
            </div>
            <div
              className="pr-download-item"
              onClick={handleDownloadAllData}
            >
              All Data
            </div>
          </div>
        )}
      </div>

      {!showResultsView ? (
        <>
          {/* Placeholder illustration screen */}
          <div className="project-rights-placeholder">
            <div className="pr-empty-animation" ref={lottieContainerRef} />
            <p className="placeholder-text">Select the filters above to see the results</p>
          </div>
        </>
      ) : (
        <>
          {/* Employee profile card */}
          <EmployeeProfileHeader employeeData={employeeData} loading={loadingProjects} />

          {loadingProjects ? (
            <Loader />
          ) : (
            <div className="project-rights-table-utility table-only">
              <TableUtility
                key={tableNonce}
                configuration={tableConfiguration}
                data={tableData}
              />
            </div>
          )}
        </>
      )}

      {/* Sticky footer — always visible */}
      <div className="pr-footer-actions">
        <button
          type="button"
          className="pr-btn pr-btn-primary"
          onClick={handleSave}
          disabled={saving || loadingProjects || !employeeData || !canUpdate}
        >
          Update
        </button>
      </div>

    </div>
  );
};

export default ProjectRights;
